export default {
  slug: "/hair-transplant-for-women",
  group: { href: "/hair", label: { en: "Hair restoration", ar: "زراعة الشعر" } },
  heroImage: "/assets/images/ai/services/hair/lashes-approved.webp",

  en: {
    title: "Hair Transplant for Women in Istanbul",
    eyebrow: "Hair restoration",
    lead:
      "Female hair loss is usually diffuse thinning rather than a bald patch, and it often has a cause that " +
      "can be treated. Transplantation is considered after those causes are excluded — and it works " +
      "differently from the male operation.",
    cta: "Book a free assessment",
    heroAlt: "Hair loss consultation for women at Platinya Clinic",
    stats: [
      { value: "1 day", label: "At the clinic" },
      { value: "No shaving", label: "In most cases" },
      { value: "12 months", label: "To judge density" },
    ],
    candidate: {
      heading: "Who it helps — and who it does not",
      intro:
        "In women the first question is not 'how many grafts' but 'why is the hair thinning'. Blood work " +
        "comes before any surgical plan.",
      items: [
        "Thinning that continues despite normal iron, thyroid, vitamin D and hormone results",
        "Localised thinning at the parting, the temples, or along the front hairline",
        "Scarring from traction, burns, surgery or alopecia that has been stable for a year",
        "Eyebrow or hairline loss after over-plucking or permanent makeup trauma",
      ],
      note:
        "Diffuse shedding across the whole scalp — telogen effluvium — is usually reversible and is not " +
        "treated with surgery. Iron deficiency, thyroid disease, PCOS and post-partum shedding are looked " +
        "for first, because transplanting into active shedding wastes grafts.",
    },
    techniques: {
      heading: "How the female operation differs",
      intro:
        "The principle is the same, the practicalities are not: less shaving, fewer grafts, more attention " +
        "to how the hair falls.",
      items: [
        {
          name: "Partial shaving or none",
          text:
            "Follicles can be extracted from a narrow band that the surrounding hair covers, so the donor " +
            "area does not have to be shaved. Slower, so sessions are smaller.",
        },
        {
          name: "Density, not coverage",
          text:
            "Female loss is about adding density between existing hairs rather than rebuilding a hairline. " +
            "Grafts are placed between native follicles at an angle that follows the natural fall.",
        },
        {
          name: "Combined with medical treatment",
          text:
            "Minoxidil, and in some cases prescribed medication, is usually continued before and after " +
            "surgery — the transplant adds density, it does not stop the underlying process.",
        },
      ],
    },
    journey: {
      heading: "From the first consultation",
      steps: [
        {
          name: "Causes first",
          text:
            "Blood tests, a history of shedding patterns, and a trichoscopy examination. If a reversible " +
            "cause is found, it is treated before surgery is discussed again.",
        },
        {
          name: "Planning the density",
          text:
            "The area is mapped and graft numbers are kept deliberately modest — over-dense packing in " +
            "thinning female scalp can damage the hair that is still there.",
        },
        {
          name: "Surgery day",
          text:
            "Local anaesthesia; the session is usually four to six hours. Most patients return home the same day.",
        },
        {
          name: "First month",
          text:
            "Swelling and tenderness settle in days. Transplanted hair sheds from week three — expected.",
        },
        {
          name: "Month 6–12",
          text: "Density builds gradually. Hair colour and texture match the surrounding hair as it matures.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro: "The risks are the standard ones, with two that matter more in women.",
      items: [
        "Shock loss of the hair around the grafts in the first weeks — usually temporary but frightening",
        "Over-dense placement damaging native follicles in a thinning scalp",
        "Patchy result if the underlying cause was still active during surgery",
        "Folliculitis, crusting and temporary numbness",
        "A visible change in hair texture at the transplanted area in a minority of patients",
      ],
      note:
        "Any clinic that offers surgery without asking about iron, thyroid and hormones is skipping the part " +
        "that decides whether the operation was worth doing.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "Will I have to shave my head?",
          a:
            "Usually not. Most female sessions use a partially shaved donor band that the surrounding hair " +
            "covers, or no shaving at all with a slower extraction.",
        },
        {
          q: "Can I wear my hair as usual afterwards?",
          a:
            "After about ten days, yes — gently. Colouring and perming wait six to eight weeks, and tight " +
            "ponytails or braids are avoided for the first month.",
        },
        {
          q: "Is one session enough?",
          a:
            "Often two sessions are planned for diffuse thinning, spaced twelve months apart, because adding " +
            "density gradually protects the existing hair.",
        },
        {
          q: "What if the cause is hormonal?",
          a:
            "Then the hormonal treatment is the foundation and surgery is only an addition. Operating while " +
            "the loss is active gives a result that thins around the grafts.",
        },
      ],
    },
    ctaBand: {
      heading: "Send your photographs for a first opinion",
      text:
        "Photos of the parting, the hairline and the donor area, plus any recent blood results, are enough " +
        "for a first view — including whether surgery is the right answer at all.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Female hair loss needs a diagnosis before any surgical plan; results vary.",
  },

  ar: {
    title: "زراعة الشعر للنساء في إسطنبول",
    eyebrow: "زراعة الشعر",
    lead:
      "تساقط الشعر عند النساء يكون عادةً خفّة منتشرة لا منطقة صلعاء، وغالباً له سبب قابل للعلاج. " +
      "تُدرَس الزراعة بعد استبعاد تلك الأسباب — وهي تعمل بشكل مختلف عن عملية الرجال.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "استشارة تساقط الشعر للنساء في عيادة بلاتينيا",
    stats: [
      { value: "يوم واحد", label: "في العيادة" },
      { value: "بلا حلاقة", label: "في معظم الحالات" },
      { value: "١٢ شهراً", label: "للحكم على الكثافة" },
    ],
    candidate: {
      heading: "من تنفعها — ومن لا تنفعه",
      intro:
        "عند النساء السؤال الأول ليس «كم بصيلة» بل «لماذا يخفّ الشعر». التحاليل تسبق أي خطة جراحية.",
      items: [
        "خفّة مستمرة رغم نتائج طبيعية للحديد والغدة الدرقية وفيتامين د والهرمونات",
        "خفّة موضعية في الفرق أو الصدغين أو خط الشعر الأمامي",
        "تساقط بندبي من الشدّ أو الحروق أو الجراحة أو الثعلبة استقرّ منذ سنة",
        "فقدان شعر الحواجب أو خط الشعر بعد نتف مفرط أو إصابة مكياج دائم",
      ],
      note:
        "التساقط المنتشر في كامل الفروة — التساقط التيلوجيني — عادةً قابل للعلاج ولا يُعالَج جراحياً. " +
        "يُبحث أولاً عن نقص الحديد وأمراض الغدة وتكيّس المبايض والتساقط بعد الولادة، لأن الزرع في تساقط نشط يهدر البصيلات.",
    },
    techniques: {
      heading: "كيف تختلف عملية النساء",
      intro: "المبدأ واحد، لكن التطبيق لا: حلاقة أقل وبصيلات أقل وعناية أكبر باتجاه سقوط الشعر.",
      items: [
        {
          name: "حلاقة جزئية أو بلا حلاقة",
          text:
            "يمكن اقتطاف البصيلات من شريط ضيّق يغطّيه الشعر المحيط، فلا تُحلق المنطقة المانحة. " +
            "أبطأ، لذلك تكون الجلسات أصغر.",
        },
        {
          name: "كثافة لا تغطية",
          text:
            "الهدف إضافة كثافة بين الشعر الموجود لا إعادة بناء خط شعر. تُوضع البصيلات بين البصيلات " +
            "الطبيعية بزاوية تتبع سقوط الشعر الطبيعي.",
        },
        {
          name: "مع العلاج الدوائي",
          text:
            "يُستمر عادةً بالمينوكسيديل، وأحياناً بأدوية بوصفة، قبل الجراحة وبعدها — الزرع يضيف كثافة " +
            "ولا يوقف العملية الأساسية.",
        },
      ],
    },
    journey: {
      heading: "من الاستشارة الأولى",
      steps: [
        {
          name: "الأسباب أولاً",
          text:
            "تحاليل دم وتاريخ نمط التساقط وفحص بالفيديو للفروة. إذا وُجد سبب قابل للعلاج يُعالَج " +
            "قبل إعادة الحديث عن الجراحة.",
        },
        {
          name: "تخطيط الكثافة",
          text:
            "تُرسم المنطقة ويُبقى عدد البصيلات متحفّظاً بقصد — التكثيف المفرط في فروة خفيفة قد يضرّ الشعر المتبقّي.",
        },
        {
          name: "يوم العملية",
          text: "تخدير موضعي؛ الجلسة عادةً أربع إلى ست ساعات. معظم المريضات يعدن لبيوتهنّ في اليوم نفسه.",
        },
        {
          name: "الشهر الأول",
          text: "التورّم والحساسية يهدآن في أيام. الشعر المزروع يتساقط من الأسبوع الثالث — وهذا متوقّع.",
        },
        {
          name: "الشهر ٦–١٢",
          text: "تبنى الكثافة تدريجياً. ويتطابق لون الشعر وملمسه مع الشعر المحيط مع نضوجه.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro: "المخاطر هي المعتادة، مع اثنتين تهمّان النساء أكثر.",
      items: [
        "تساقط صدمي في الشعر المحيط بالبصيلات في الأسابيع الأولى — مؤقّت عادةً لكنه مخيف",
        "التكثيف المفرط قد يضرّ البصيلات الطبيعية في فروة خفيفة",
        "نتيجة غير متساوية إذا كان السبب الأساسي نشطاً وقت العملية",
        "التهاب جريبات وقشور وتنميل مؤقّت",
        "تغيّر ملحوظ في ملمس الشعر في المنطقة المزروعة عند نسبة قليلة من المريضات",
      ],
      note:
        "أي عيادة تعرض الجراحة دون السؤال عن الحديد والغدة والهرمونات تتجاوز الجزء الذي يحدّد إن كانت العملية تستحق.",
    },
    faq: {
      heading: "أسئلة تطرحها المريضات",
      items: [
        {
          q: "هل يجب حلاقة رأسي؟",
          a:
            "عادةً لا. معظم جلسات النساء تستخدم شريطاً مانحاً محلوقاً جزئياً يغطّيه الشعر المحيط، " +
            "أو بلا حلاقة إطلاقاً مع اقتطاف أبطأ.",
        },
        {
          q: "هل أعود لتصفيف شعري كالمعتاد؟",
          a:
            "بعد نحو عشرة أيام نعم بلطف. الصباغة والبيرم ينتظران ستة إلى ثمانية أسابيع، " +
            "وتُجتنب ربطات الشعر المشدودة والضفائر في الشهر الأول.",
        },
        {
          q: "هل تكفي جلسة واحدة؟",
          a:
            "غالباً تُخطَّط جلستان للخفّة المنتشرة بفاصل ١٢ شهراً، لأن إضافة الكثافة تدريجياً تحمي الشعر الموجود.",
        },
        {
          q: "وإذا كان السبب هرمونياً؟",
          a:
            "حينها العلاج الهرموني هو الأساس والجراحة إضافة فقط. الجراحة أثناء تساقط نشط تعطي نتيجة تخفّ حول البصيلات.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسلي صورك لرأي أوّلي",
      text:
        "صور للفرق وخط الشعر والمنطقة المانحة، مع أي تحاليل حديثة، تكفي لرأي أولي — " +
        "بما في ذلك هل الجراحة هي الجواب الصحيح أصلاً.",
      button: "أرسلي صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. تساقط الشعر عند النساء يحتاج تشخيصاً قبل أي خطة جراحية؛ والنتائج تختلف.",
  },
};
